import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Alert, Button, Card, Checkbox, Col, DatePicker, Descriptions, Form, Input, InputNumber,
    Radio, Row, Select, Space, Steps, Typography,
} from 'antd';
import dayjs from 'dayjs';
import API from 'app/utils/api/axios';
import PageWithLoader from 'app/hoc/page_with_loader/page_with_loader';
import API_URL from 'app/constants/api_urls';
import Signature from './signature';

type Value = string | number | undefined;
type Answers = Record<string, Value>;
type EventKind = 'group' | 'virtual';
type InsuranceEvent = Answers & { kind: EventKind };
type Field = {
    key: string;
    label: string;
    kind: 'text' | 'radio' | 'select' | 'date' | 'integer' | 'money';
    choices: string[] | null;
    when: Record<string, string> | null;
    help: string | null;
};
type Program = {
    max_event_date: string;
    events: Record<EventKind, Field[]>;
    organization: Field[];
    coverage: Field[];
    declaration: string;
};
type Quote = {
    pk: number;
    events: InsuranceEvent[];
    answers: Answers;
    date_signed: string | null;
    date_updated: string;
};

const STEPS = ['Program details', 'Events', 'Organization & coverage', 'Review & sign'];
const QUERY_KEY = ['insurance-quotes'];
const isVisible = (field: Field, answers: Answers) =>
    Object.entries(field.when ?? {}).every(([key, value]) => answers[key] === value);

const firstMissing = (fields: Field[], answers: Answers) =>
    fields.find((field) => isVisible(field, answers) && String(answers[field.key] ?? '').trim() === '');

const setAnswer = (fields: Field[], answers: Answers, key: string, value: Value) => {
    const updated = { ...answers, [key]: value };
    fields.forEach((field) => {
        if (!isVisible(field, updated)) delete updated[field.key];
    });
    return updated;
};

const FieldControl = ({
    field, values, maxDate, onChange, inputId,
}: { field: Field; values: Answers; maxDate: string; onChange: (value: Value) => void; inputId: string }) => {
    const value = values[field.key];
    const id = inputId;
    let control;
    if (field.kind === 'radio') {
        control = <Radio.Group id={id} aria-label={field.label} options={field.choices ?? []}
            value={value} onChange={(event) => onChange(event.target.value)} />;
    } else if (field.kind === 'select') {
        control = <Select id={id} showSearch optionFilterProp='label' placeholder='Select an option'
            options={field.choices?.map((choice) => ({ label: choice, value: choice }))}
            value={value} onChange={onChange} />;
    } else if (field.kind === 'date') {
        control = <DatePicker id={id} style={{ width: '100%' }}
            value={value ? dayjs(value) : null}
            maxDate={dayjs(maxDate)}
            disabledDate={(date) => date.isAfter(dayjs(maxDate), 'day') ||
                (field.key === 'end_date' && !!values.start_date && date.isBefore(dayjs(values.start_date), 'day'))}
            onChange={(date) => onChange(date?.format('YYYY-MM-DD'))} />;
    } else if (field.kind === 'integer' || field.kind === 'money') {
        control = <InputNumber id={id} style={{ width: '100%' }}
            value={typeof value === 'number' ? value : null}
            min={field.kind === 'money' ? 0 : 1} max={2147483647} precision={0}
            onChange={(number) => onChange(number ?? undefined)} />;
    } else {
        control = <Input id={id} maxLength={500} value={value}
            onChange={(event) => onChange(event.target.value)} />;
    }
    return <Form.Item label={field.label} htmlFor={id} required extra={field.help}>{control}</Form.Item>;
};

const ProgramDetails = () => (
    <Space direction='vertical' size='middle'>
        <Typography.Paragraph>
            Nicholas Hill Group’s 2026–2027 running program provides event liability and participant accident insurance
            for eligible US running events.
        </Typography.Paragraph>
        <Descriptions column={1} bordered size='small' items={[
            { key: 'limits', label: 'Liability limits', children: '$1M per occurrence / $2M aggregate or $2M / $4M' },
            { key: 'changes', label: 'After purchase', children: 'Active policies cannot be amended to add or cancel events, change dates or add optional coverage. New events require a separate policy.' },
            { key: 'cancellations', label: 'Cancellation', children: 'Cancellation is permitted only before coverage starts. Fees are non-refundable.' },
        ]} />
        <Alert type='info' showIcon message='Coverage exclusions' description={
            <Space direction='vertical'>
                <span>Triathlons, duathlons, multisport and adventure races; cycling, swimming, paddling and watercraft; events with animals other than documented service animals; fireworks, rappelling, trampolines, zip lines, aviation, motorsports, shooting and personal or household events.</span>
                <span>Medical personnel, including volunteers, EMTs and athletic trainers, and their professional acts are excluded.</span>
            </Space>
        } />
        <Typography.Text type='secondary'>
            Already insured? Use the <a href='https://rdhq-new.nicholashillgroup.com/dashboard'>policy dashboard</a>.
            For policies purchased before May 8, 2025, use the <a href='https://atra.nicholashillgroup.com/dashboard'>legacy login</a>.
        </Typography.Text>
    </Space>
);

const QuoteWizard = ({ program, quotes }: { program: Program; quotes: Quote[] }) => {
    const queryClient = useQueryClient();
    const [quote, setQuote] = useState<Quote | undefined>(quotes[0]);
    const [events, setEvents] = useState<InsuranceEvent[]>(quotes[0]?.events ?? []);
    const [answers, setAnswers] = useState<Answers>(quotes[0]?.answers ?? {});
    const [step, setStep] = useState(0);
    const [error, setError] = useState('');
    const [signature, setSignature] = useState('');
    const [birthdate, setBirthdate] = useState('');
    const [accepted, setAccepted] = useState(false);
    const [saved, setSaved] = useState(false);
    const save = useMutation({
        mutationFn: async (sign: boolean) => {
            const payload = {
                events, answers,
                ...(sign ? { applicant_birthdate: birthdate, declaration_signature: signature, declaration_accepted: accepted } : {}),
            };
            const { data } = quote
                ? await API.patch<Quote>(`${API_URL.INSURANCE_QUOTES}${quote.pk}/`, payload)
                : await API.post<Quote>(API_URL.INSURANCE_QUOTES, payload);
            return data;
        },
        onSuccess: (data) => {
            setQuote(data);
            setSaved(true);
            if (data.date_signed) {
                setBirthdate('');
                setSignature('');
                setAccepted(false);
            }
            queryClient.setQueryData<Quote[]>(QUERY_KEY, (existing) =>
                [data, ...(existing ?? []).filter((item) => item.pk !== data.pk)]);
        },
    });
    const reset = (selected?: Quote) => {
        setQuote(selected);
        setEvents(selected?.events ?? []);
        setAnswers(selected?.answers ?? {});
        setStep(0);
        setError('');
        setSignature('');
        setBirthdate('');
        setAccepted(false);
        setSaved(false);
    };
    const eventType = events.some((event) => event.kind === 'group')
        ? (events.some((event) => event.kind === 'virtual') ? 'Group and virtual events' : 'Group events')
        : 'Virtual events';

    const submit = async (action: 'next' | 'draft' | 'sign') => {
        setError('');
        setSaved(false);
        if (action !== 'draft') {
            if (step === 1) {
                if (!events.length) { setError('Add at least one event.'); return; }
                for (const [index, event] of events.entries()) {
                    const missing = firstMissing(program.events[event.kind], event);
                    if (missing) { setError(`Event ${index + 1}: complete “${missing.label}”.`); return; }
                    if (String(event.start_date) > String(event.end_date)) {
                        setError(`Event ${index + 1}: the end date cannot precede the start date.`); return;
                    }
                }
            }
            if (step === 2) {
                const missing = firstMissing([...program.organization, ...program.coverage], answers);
                if (missing) { setError(`Complete “${missing.label}” before continuing.`); return; }
            }
            if (action === 'sign' && (!birthdate || !signature || !accepted)) {
                setError('Enter your birth date, draw your signature and accept the declaration.'); return;
            }
        }
        if (step === 0 && action === 'next') { setStep(1); return; }
        try {
            await save.mutateAsync(action === 'sign');
            if (action === 'next') setStep(step + 1);
        } catch {
            setError('We couldn’t save the quote. Check the required fields and date ranges, then try again.');
        }
    };

    const renderFields = (fields: Field[], values: Answers, update: (values: Answers) => void, prefix: string) => (
        <Row gutter={24}>
            {fields.filter((field) => isVisible(field, values)).map((field) => (
                <Col xs={24} md={field.kind === 'radio' ? 24 : 12} key={field.key}>
                    <FieldControl field={field} values={values} maxDate={program.max_event_date}
                        onChange={(value) => update(setAnswer(fields, values, field.key, value))}
                        inputId={`${prefix}-${field.key}`} />
                </Col>
            ))}
        </Row>
    );

    const review = (fields: Field[], values: Answers) => (
        <Descriptions column={1} size='small' bordered items={fields
            .filter((field) => isVisible(field, values))
            .map((field) => ({ key: field.key, label: field.label, children: values[field.key] ?? '—' }))} />
    );

    return (
        <Space direction='vertical' size='large' style={{ width: '100%' }}>
            <Space wrap>
                {!!quotes.length && <Select aria-label='Saved applications' placeholder='Saved applications'
                    style={{ minWidth: 220 }} value={quote?.pk} disabled={save.isPending}
                    options={quotes.map((item) => ({
                        value: item.pk,
                        label: `${item.answers.organization || 'Application'} · ${item.date_signed ? 'Signed' : 'Draft'} #${item.pk}`,
                    }))}
                    onChange={(pk) => reset(quotes.find((item) => item.pk === pk))} />}
                {!!quote && <Button disabled={save.isPending} onClick={() => reset()}>Start a new quote</Button>}
            </Space>
            {quote?.date_signed ? (
                <Card title='Signed application saved'>
                    <Alert type='success' showIcon message='Your application is saved to your dashboard.'
                        description='It has not been sent to Nicholas Hill Group or priced. No coverage has been purchased.' />
                    {review([...program.organization, ...program.coverage], answers)}
                    {events.map((event, index) => (
                        <Card title={event.name || `Event ${index + 1}`} key={index}>
                            {review(program.events[event.kind], event)}
                        </Card>
                    ))}
                </Card>
            ) : (
                <>
                    <Steps current={step} size='small' items={STEPS.map((title) => ({ title }))} />
                    <Card title={STEPS[step]}>
                        <Form layout='vertical' disabled={save.isPending}>
                            {step === 0 && <ProgramDetails />}
                            {step === 1 && <Space direction='vertical' size='middle' style={{ width: '100%' }}>
                                <Typography.Paragraph>Add each in-person or virtual running event you want to insure. Events on one policy share the same limits.</Typography.Paragraph>
                                {events.map((event, index) => (
                                    <Card key={index} size='small' title={`${event.kind === 'group' ? 'Group' : 'Virtual'} event ${index + 1}`}
                                        extra={<Button type='link' danger onClick={() => setEvents(events.filter((_, i) => i !== index))}>Remove</Button>}>
                                        {renderFields(program.events[event.kind], event, (next) => {
                                            setEvents(events.map((item, i) => i === index ? { ...next, kind: event.kind } : item));
                                        }, `event-${index}`)}
                                    </Card>
                                ))}
                                <Space wrap>
                                    <Button disabled={events.length >= 100} onClick={() => setEvents([...events, { kind: 'group' }])}>Add group event</Button>
                                    <Button disabled={events.length >= 100} onClick={() => setEvents([...events, { kind: 'virtual' }])}>Add virtual event</Button>
                                </Space>
                            </Space>}
                            {step === 2 && <>
                                <Typography.Title level={4}>Organization</Typography.Title>
                                {renderFields([...program.organization, ...program.coverage], answers, setAnswers, 'answer')}
                                <Typography.Text type='secondary'>Event type: {eventType}. To change it, edit the events on the previous step.</Typography.Text>
                            </>}
                            {step === 3 && <Space direction='vertical' size='middle' style={{ width: '100%' }}>
                                <Typography.Title level={4}>Organization & coverage</Typography.Title>
                                {review([...program.organization, ...program.coverage], answers)}
                                {events.map((event, index) => (
                                    <Card size='small' key={index} title={`${event.kind === 'group' ? 'Group' : 'Virtual'} event ${index + 1}`}>
                                        {review(program.events[event.kind], event)}
                                    </Card>
                                ))}
                                <Alert type='info' showIcon message='Saving an application does not request a price or purchase coverage.' />
                                <Typography.Title level={4}>Applicant declaration</Typography.Title>
                                <Typography.Paragraph>{program.declaration}</Typography.Paragraph>
                                <Form.Item label='Applicant date of birth' htmlFor='insurance-birthdate' required extra='The applicant must be at least 18 years old.'>
                                    <DatePicker id='insurance-birthdate' value={birthdate ? dayjs(birthdate) : null}
                                        maxDate={dayjs().subtract(18, 'year')} minDate={dayjs().subtract(131, 'year').add(1, 'day')}
                                        onChange={(date) => { setBirthdate(date?.format('YYYY-MM-DD') ?? ''); setAccepted(false); }} />
                                </Form.Item>
                                <Signature value={signature} onChange={(value) => { setSignature(value); setAccepted(false); }} />
                                <Checkbox checked={accepted} onChange={(event) => setAccepted(event.target.checked)}>
                                    I have read and agree to the applicant declaration.
                                </Checkbox>
                            </Space>}
                        </Form>
                    </Card>
                    {error && <Alert type='error' showIcon message={error} />}
                    {saved && !error && <Typography.Text type='secondary'>Application saved.</Typography.Text>}
                    <Space wrap>
                        {step > 0 && <Button disabled={save.isPending} onClick={() => {
                            setStep(step - 1); setError(''); setSignature(''); setAccepted(false);
                        }}>Back</Button>}
                        {step > 0 && <Button disabled={save.isPending} onClick={() => submit('draft')}>Save draft</Button>}
                        <Button type='primary' loading={save.isPending} onClick={() => submit(step === 3 ? 'sign' : 'next')}>
                            {step === 3 ? 'Save signed application' : 'Continue'}
                        </Button>
                    </Space>
                </>
            )}
        </Space>
    );
};

const EventInsurance = () => {
    const program = useQuery({
        queryKey: ['insurance-program'],
        queryFn: async ({ signal }) => (await API.get<Program>(API_URL.INSURANCE_PROGRAM, { signal })).data,
    });
    const quotes = useQuery({
        queryKey: QUERY_KEY,
        queryFn: async ({ signal }) => (await API.get<Quote[]>(API_URL.INSURANCE_QUOTES, { signal })).data,
    });
    return (
        <PageWithLoader isLoading={program.isPending || quotes.isPending} error={program.isError || quotes.isError}>
            <div className='page__header'>
                <div>
                    <Typography.Title level={1}>Event insurance</Typography.Title>
                    <Typography.Text>Prepare a quote application for your US running events.</Typography.Text>
                </div>
            </div>
            <div style={{ marginTop: 24 }}>
                {program.data && quotes.data && <QuoteWizard program={program.data} quotes={quotes.data} />}
            </div>
        </PageWithLoader>
    );
};

export default EventInsurance;
